import { TpExpressionContext, evalExpression } from '../../../../components/safeExpressions';
import { getCanonicalTypeInfo, getColumnInfo } from '../../../../data/analyticsDataSlice';
import { isEncoded } from '../../../../data/interfaces';
import type { TpCanonicalTypeInfo, TpDataContext, TpDerivedColumnPatch } from '../../../../data/interfaces';
import { getDataTable } from '../../charts/chartDataBindings';
import { convertColumnDataListToDisplayString, isSelectedItem } from '../../helpers/misc';
import { TpDashboardActionContext, TpDbActionStepDefinition as TpDbActionStepTypeDefinition } from '../interfaces';
import {
  addFunctionsToExpressionContext,
  defaultExpressionFunctionsList,
  getExpressionFunctionsDescription,
} from './dbActionStepTypeCalculateVariable';
import { mapCanonicalTypeInfoToSelectionDTO } from '../../../../data/selection/snapshot/selectionFilterSerialization.serialize';
import { createAddColumnAction, createColumnMetadata } from '../../../../data/mutation-ledger/schemas';
import type { AddColumnFromExpression, JsonValue } from '../../../../data/mutation-ledger/schemas';
import { validateAndPrepareFilterStepsForTarget } from '../../../../data/selection/snapshot/selectionFilterSerialization.deserialize';
import { applySelectionFilterToCanonicalTypeInfo } from '../../../../data/selection/snapshot/dataContextSelectionSnapshot';
import { addTableColumn, buildDerivedColumnPatch } from '../helpers/derivedColumnPatch';

const paramNameSelectedState = '_selectedState_';
// guid() is non-deterministic, so CalculateColumn excludes it to keep replay deterministic.
// CalculateVariable intentionally retains the default function list.
const calculateColumnExpressionFunctions = defaultExpressionFunctionsList.filter((func) => func.name !== 'guid');

function getJsonSchema(): any {
  return {
    type: 'object',
    title: 'Calculate Column',
    description: 'Creates a new column (property) in a datatable by evaluating an expression.',
    properties: {
      type: { type: 'string', const: 'CalculateColumn' },
      dataTableId: {
        type: 'string',
        description: 'Identifier of the data table for which a new column should be calculated.',
      },
      usedColIds: {
        type: 'array',
        items: { type: 'string' },
        description: 'List of the existing column used in the expression.',
      },
      newColId: {
        type: 'string',
        description: 'Identifier of the new column.',
      },
      newColName: {
        type: 'string',
        description: 'Name of the new column.',
      },
      newColDescription: {
        type: 'string',
        description: 'Description of the new column.',
      },
      newColDataType: {
        type: 'string',
        description: 'Data type of the new column to be created.',
      },
      newGroupId: {
        type: 'string',
        description: 'Identifier of the group for the new column.',
      },
      newGroupName: {
        type: 'string',
        description: 'Name of the group for the new column (can be absent if that group already exists).',
      },
      newGroupDescription: {
        type: 'string',
        description: 'Description of the group for the new column (can be absent if that group already exists).',
      },
      expression: {
        type: 'string',
        description: `Expression to be evaluated. This can contain already existing variables, prefixed by a $ sign.
Special functions and parameters: ${getExpressionFunctionsDescription(
          calculateColumnExpressionFunctions,
        )}, ${paramNameSelectedState}`,
      },
    },
    required: ['type', 'dataTableId', 'usedColIds', 'newColId', 'newColDataType', 'expression'],
  };
}

export function computeCalculatedColumnPatch(
  dataContext: TpDataContext,
  payload: AddColumnFromExpression & {
    targetTableId: string;
    targetColumnId: string;
    selectedItemsMap: { [itemId: string]: boolean } | null;
  },
): TpDerivedColumnPatch {
  const dataTable = getDataTable(dataContext, payload.targetTableId);
  const itemList = dataTable.columnData[dataTable.colItemId];
  const usedColumnData: { [colId: string]: any[] } = {};
  for (const colId of payload.usedColIds) {
    const colInfo = getColumnInfo(dataTable, colId);
    let columnData = dataTable.columnData[colId];
    if (isEncoded(colInfo.valueType)) columnData = convertColumnDataListToDisplayString(colInfo, columnData);
    usedColumnData[colId] = columnData;
  }
  const newColData = itemList.map((itemId: string, itemIdx: number) => {
    const parameters: { [id: string]: any } = {};
    for (const [name, value] of Object.entries(payload.variables)) parameters['$' + name] = value;
    for (const colId of payload.usedColIds) parameters[colId] = usedColumnData[colId][itemIdx];
    parameters[payload.paramNameSelectedState] = isSelectedItem(payload.selectedItemsMap, itemId);
    const expr: TpExpressionContext = { expressionString: payload.expression, parameters };
    addFunctionsToExpressionContext(expr, calculateColumnExpressionFunctions);
    return evalExpression(expr);
  });
  const metadata = payload.columnMetadata;
  return buildDerivedColumnPatch(
    payload.targetTableId,
    payload.targetColumnId,
    metadata.name,
    metadata.description,
    metadata.valueType,
    metadata.groupId,
    metadata.groupName,
    metadata.groupDescription,
    newColData,
    metadata.decimalDigits,
  );
}

/**
 * Expand the frozen selection snapshot into a canonical type info,
 * and compute the derived column patch based on that.
 */
export function computeCalculatedColumnPatchFromFrozenSelection(
  dataContext: TpDataContext,
  payload: AddColumnFromExpression & { targetTableId: string; targetColumnId: string },
): { patch: TpDerivedColumnPatch; warnings: string[] } {
  const dataTable = getDataTable(dataContext, payload.targetTableId);
  const targetCanonicalType = getCanonicalTypeInfo(dataContext, dataTable.canonicalTypeId);

  // Create a scratch copy of the canonical type info to apply the frozen selection snapshot to,
  // so that we can evaluate the expression with the correct selection state.
  const scratch: TpCanonicalTypeInfo = {
    ...targetCanonicalType,
    itemIdList: dataTable.columnData[dataTable.colItemId],
    selectedItemsMap: null,
    selectionFilter: { steps: [], manualSelection: false },
    restrictionFilter: null,
    restrictedItemsMap: null,
    restrictionList: null,
    restrictedItemId2Inxdex: {},
  };
  let selectedItemsMap: { [itemId: string]: boolean } | null = null;
  let warnings: string[] = [];
  if (payload.frozenSelectionSnapshot) {
    const sanitizedSelection = validateAndPrepareFilterStepsForTarget(
      dataContext,
      scratch,
      payload.frozenSelectionSnapshot.selectionFilter.steps,
      payload.frozenSelectionSnapshot.manualToggleIds,
      dataContext.assetId,
      '',
    );
    applySelectionFilterToCanonicalTypeInfo(dataContext, scratch, {
      filter: {
        steps: sanitizedSelection.sanitizedSteps,
        manualSelection: sanitizedSelection.sanitizedManualToggleIds.length > 0,
      },
      manualToggleIds: sanitizedSelection.sanitizedManualToggleIds,
    });
    selectedItemsMap = scratch.selectedItemsMap;
    if (sanitizedSelection.warnings.length > 0) {
      warnings = [
        `Calculation of column ${payload.columnMetadata.name} will not match the original column due to incompatible selection steps.`,
      ];
    }
  }
  const patch = computeCalculatedColumnPatch(dataContext, { ...payload, selectedItemsMap });
  return { patch, warnings };
}

function execute(ctx: TpDashboardActionContext, step: any, onCompleted: () => void) {
  const dataTableId = step.dataTableId as string;
  const newColId = step.newColId as string;
  const datanewColDataType = step.newColDataType as string;
  const expression = step.expression as string;
  const dataTable = getDataTable(ctx.dataContext, dataTableId);
  const usedColIds = step.usedColIds as string[];

  const canonicalTypeInfo = getCanonicalTypeInfo(ctx.dataContext, dataTable.canonicalTypeId);

  const variables = Object.fromEntries(
    Object.entries(ctx.variables).map(([name, variable]) => [name, variable.content]),
  ) as Record<string, JsonValue>;
  const serializedVariables = JSON.parse(JSON.stringify(variables)) as Record<string, JsonValue>;
  const ledgerAction = createAddColumnAction({
    targetTableId: dataTableId,
    targetColumnIds: [newColId],
    payload: {
      type: 'EXPRESSION',
      expression,
      paramNameSelectedState,
      usedColIds,
      variables: serializedVariables,
      ...(expression.includes(paramNameSelectedState)
        ? { frozenSelectionSnapshot: mapCanonicalTypeInfoToSelectionDTO(ctx.dataContext, canonicalTypeInfo) }
        : {}),
      columnMetadata: createColumnMetadata({
        name: step.newColName as string,
        description: step.newColDescription,
        valueType: datanewColDataType as any,
        groupId: step.newGroupId as string | null,
        groupName: step.newGroupName as string | null,
        groupDescription: step.newGroupDescription,
      }),
    },
  });
  const computedPatch = computeCalculatedColumnPatch(ctx.dataContext, {
    ...(ledgerAction.payload as AddColumnFromExpression),
    targetTableId: dataTableId,
    targetColumnId: newColId,
    selectedItemsMap: canonicalTypeInfo.selectedItemsMap,
  });
  const newColData = computedPatch.columnData;
  addTableColumn(
    ctx,
    dataTableId,
    newColId,
    step.newColName as string,
    step.newColDescription as string | null,
    datanewColDataType,
    step.newGroupId as string | null,
    step.newGroupName as string | null,
    step.newGroupDescription as string | null,
    newColData,
    null,
    ledgerAction,
  );
  onCompleted();
}

const dbActionStepTypeCalculateColumn: TpDbActionStepTypeDefinition = {
  stepType: 'CalculateColumn',
  execute,
  getJsonSchema,
};

export default dbActionStepTypeCalculateColumn;
